import React, { useState, useEffect } from 'react';
import { isTrialExpired, User, loadData } from './storage';
import * as authApi from './authApi';
import { setAccountRole } from './api';
import Landing from './screens/Landing';
import ClassroomEntrance from './screens/ClassroomEntrance';
import SignupWizard from './screens/SignupWizard';
import Login from './screens/Login';
import ForgotPassword from './screens/ForgotPassword';
import ResetPassword from './screens/ResetPassword';
import Onboarding from './screens/Onboarding';
import Dashboard from './screens/Dashboard';
import TalkAboutStudent from './screens/TalkAboutStudent';
import UrgencyMode from './screens/UrgencyMode';
import MyStudents from './screens/MyStudents';
import StudentProfile from './screens/StudentProfile';
import Library from './screens/Library';
import LibraryTopic from './screens/LibraryTopic';
import CommunicationHelper from './screens/CommunicationHelper';
import BurnoutTracker from './screens/BurnoutTracker';
import FridayDebrief from './screens/FridayDebrief';
import WinsBoard from './screens/WinsBoard';
import Paywall from './screens/Paywall';
import StrategyCheckIn from './screens/StrategyCheckIn';
import DailyRating from './screens/DailyRating';
import ComingSoon from './screens/ComingSoon';
import Admin from './screens/Admin';
import Billing from './screens/Billing';
import Account from './screens/Account';
import Terms from './screens/Terms';
import Privacy from './screens/Privacy';
import Contact from './screens/Contact';
import Feedback from './screens/Feedback';
import Demo from './screens/Demo';
import Schools from './screens/Schools';
import Tour from './screens/Tour';
import Accept from './screens/Accept';
import Footer from './components/Footer';
import './index.css';

export type Screen = 'entrance'|'landing'|'demo'|'schools'|'tour'|'accept'|'signup'|'login'|'forgot'|'reset-password'|'onboarding'|'dashboard'|'talk'|'urgency'|'students'|'student-profile'|'library'|'library-topic'|'communication'|'burnout'|'friday'|'wins'|'paywall'|'strategy-checkin'|'daily-rating'|'coming-soon'|'admin'|'billing'|'account'|'terms'|'privacy'|'contact'|'feedback';
export interface AppState { currentStudentId?: string; currentLibraryTopic?: string; intent?: 'subscribe'; accountType?: 'teacher' | 'parent'; }

export default function App() {
  const [screen, setScreen] = useState<Screen>('landing');
  const [appState, setAppState] = useState<AppState>({});
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [checkoutReturn, setCheckoutReturn] = useState(false);

  useEffect(() => {
    // Public product walkthrough at /demo — renders without any auth check, so a
    // visitor (or a shared link) lands straight on the tour. Netlify serves the
    // SPA for this path via a targeted redirect in netlify.toml.
    if (window.location.pathname.replace(/\/$/, '') === '/demo') {
      setScreen('demo');
      setBooting(false);
      return;
    }
    // Public "request a school license" page. Renders without any auth check so a
    // direct visit or shared link lands straight on the form (netlify.toml serves
    // the SPA shell for /schools).
    if (window.location.pathname.replace(/\/$/, '') === '/schools') {
      setScreen('schools');
      setBooting(false);
      return;
    }
    // Public 90-second walkthrough for school leaders.
    if (window.location.pathname.replace(/\/$/, '') === '/tour') {
      setScreen('tour');
      setBooting(false);
      return;
    }
    // Public "accept your quote" page (from the school quote email). The lead id
    // is read from the query string by the Accept screen.
    if (window.location.pathname.replace(/\/$/, '') === '/accept') {
      setScreen('accept');
      setBooting(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('reset');
    if (rt) {
      setResetToken(rt);
      setScreen('reset-password');
      window.history.replaceState({}, '', '/');
      setBooting(false);
      return;
    }
    const cameFromCheckout = params.get('checkout') === 'success';
    if (params.get('checkout')) window.history.replaceState({}, '', '/');
    authApi.me().then(async u => {
      if (u) {
        // Authenticated users bypass the entrance entirely (existing routing).
        await loadData();
        setAccountRole(u.role); // Parent Mode: frame the AI data by role (client-side labels)
        setUser(u);
        if (cameFromCheckout && !u.isPaid) setCheckoutReturn(true);
        setScreen(isTrialExpired(u) && !u.isPaid ? 'paywall' : 'dashboard');
      } else {
        // First-visit signed-out → cinematic entrance; afterwards, straight to
        // the marketing homepage (a per-device flag so it isn't forced repeatedly).
        let seen = false;
        try { seen = !!localStorage.getItem('dms_entrance_seen'); } catch { /* ignore */ }
        if (!seen) setScreen('entrance');
      }
      setBooting(false);
    });
  }, []);

  const navigate = (s: Screen, state?: AppState) => { setScreen(s); if (state) setAppState(state); window.scrollTo(0, 0); };
  const refreshUser = async () => { const u = await authApi.me(); if (u) await loadData(); setAccountRole(u?.role); setUser(u); return u; };
  const props = { navigate, appState, user, refreshUser, resetToken, checkoutReturn };

  if (booting) {
    return (
      <div className="page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  // The immersive entrance owns the full viewport (no app frame, no footer).
  if (screen === 'entrance') return <ClassroomEntrance {...props} />;

  // The homepage is a full-width marketing page — not confined to the app frame.
  // The footer rides along beneath it, full width.
  if (screen === 'landing') return <><Landing {...props} /><Footer navigate={navigate} /></>;

  // The /demo walkthrough is a full-width public tour — same treatment as the
  // homepage (no app frame, footer beneath), and reachable without a login.
  if (screen === 'demo') return <><Demo {...props} /><Footer navigate={navigate} /></>;

  // Desktop width tier per screen: forms stay narrow (default 480), reading-heavy
  // screens get a comfortable measure (~680), and list/grid screens get room.
  const WIDE = new Set<Screen>(['dashboard', 'students', 'library', 'admin']);
  const READ = new Set<Screen>(['talk', 'library-topic', 'communication', 'friday', 'student-profile', 'wins', 'burnout', 'coming-soon', 'terms', 'privacy', 'schools', 'tour', 'accept']);
  const pageClass = 'page' + (WIDE.has(screen) ? ' page-wide' : READ.has(screen) ? ' page-read' : '');

  return (
    <div className={pageClass}>
      {screen === 'signup' && <SignupWizard {...props} />}
      {screen === 'login' && <Login {...props} />}
      {screen === 'forgot' && <ForgotPassword {...props} />}
      {screen === 'reset-password' && <ResetPassword {...props} />}
      {screen === 'onboarding' && <Onboarding {...props} />}
      {screen === 'dashboard' && <Dashboard {...props} />}
      {screen === 'talk' && <TalkAboutStudent {...props} />}
      {screen === 'urgency' && <UrgencyMode {...props} />}
      {screen === 'students' && <MyStudents {...props} />}
      {screen === 'student-profile' && <StudentProfile {...props} />}
      {screen === 'library' && <Library {...props} />}
      {screen === 'library-topic' && <LibraryTopic {...props} />}
      {screen === 'communication' && <CommunicationHelper {...props} />}
      {screen === 'burnout' && <BurnoutTracker {...props} />}
      {screen === 'friday' && <FridayDebrief {...props} />}
      {screen === 'wins' && <WinsBoard {...props} />}
      {screen === 'paywall' && <Paywall {...props} />}
      {screen === 'strategy-checkin' && <StrategyCheckIn {...props} />}
      {screen === 'daily-rating' && <DailyRating {...props} />}
      {screen === 'coming-soon' && <ComingSoon {...props} />}
      {screen === 'admin' && <Admin {...props} />}
      {screen === 'billing' && <Billing {...props} />}
      {screen === 'account' && <Account {...props} />}
      {screen === 'terms' && <Terms {...props} />}
      {screen === 'privacy' && <Privacy {...props} />}
      {screen === 'contact' && <Contact {...props} />}
      {screen === 'feedback' && <Feedback {...props} />}
      {screen === 'schools' && <Schools {...props} />}
      {screen === 'tour' && <Tour {...props} />}
      {screen === 'accept' && <Accept {...props} />}
      <Footer navigate={navigate} />
    </div>
  );
}
